<template>
    <div class="d-flex-grow justify-center">
         <v-form @submit.prevent="store" ref="formRef" v-model="formValid" class="w-100 w-md-50rem max-w-100">
             <v-card class="d-flex-col-grow">
                 <v-card-text class="flex-grow-1">
                     <div class="d-flex flex-column ga-7">
                         <div class="fs-2 fw-medium">
                             Company Information
                         </div>
                         <div v-if="form.errors.custom_error" class="fs-1 text-error">
                             {{ form.errors.custom_error }}
                         </div>
                         <div>
                             <v-text-field-v1
                                 label="Company Name"
                                 v-model="form.name"
                                 placeholder=""
                                 required
                                 :error-messages="form.errors.name"
                             ></v-text-field-v1>
                         </div>
                         <div>
                            <v-row>
                                <v-col cols="12">
                                     <div>
                                         <v-text-field-v1
                                             label="Street address"
                                             v-model="form.street_address"
                                             placeholder=""
                                             :error-messages="form.errors.street_address"
                                         ></v-text-field-v1>
                                     </div>
                                 </v-col>
                                 <v-col cols="12" sm="6" lg="3">
                                     <div>
                                         <v-text-field-v1
                                             label="City"
                                             v-model="form.city"
                                             placeholder=""
                                             :error-messages="form.errors.city"
                                         ></v-text-field-v1>
                                     </div>
                                 </v-col>
                                 <v-col cols="12" sm="6" lg="3">
                                     <div>
                                         <v-text-field-v1
                                             label="State"
                                             v-model="form.state"
                                             placeholder=""
                                             :error-messages="form.errors.state"
                                         ></v-text-field-v1>
                                     </div>
                                 </v-col>
                                 <v-col cols="12" sm="6" lg="3">
                                     <div>
                                         <v-text-field-v1
                                             label="Postal code"
                                             v-model="form.postal_code"
                                             placeholder=""
                                             :error-messages="form.errors.postal_code"
                                         ></v-text-field-v1>
                                     </div>
                                 </v-col>
                                 <v-col cols="12" sm="6" lg="3">
                                     <div>
                                         <v-text-field-v1
                                             label="Country"
                                             v-model="form.country"
                                             placeholder=""
                                             :error-messages="form.errors.country"
                                         ></v-text-field-v1>
                                     </div>
                                 </v-col>
                            </v-row>
                         </div>
                         <div>
                             <v-text-field-v1
                                 :label="`${biller.additional_data?.template_signor_placeholder_name} Name`"
                                 v-model="form.placeholder_name"
                                 placeholder=""
                                 required
                                 :error-messages="form.errors.placeholder_name"
                             ></v-text-field-v1>
                         </div>
                         <div>
                             <v-text-field-v1
                                 :label="`${biller.additional_data?.template_signor_placeholder_name} Email`"
                                 v-model="form.placeholder_email"
                                 placeholder=""
                                 required
                                 :error-messages="form.errors.placeholder_email"
                             ></v-text-field-v1>
                         </div>
                         <div class="well">
                            <div class="fw-medium fs-1 pb-2">Agreement Information:</div>
                            <div class="text-note pb-6">Note: Multiple fields may require the same value in order to maintain the dynamic nature of the document. Please provide the values accordingly.</div>
                            <div class="d-flex flex-column ga-4">
                                <template v-for="(value, key) in templateVariables" :key="key">
                                    <div v-if="!value.map || value.map == 'user_input'">
                                        <v-text-field-v1
                                            density="comfortable"
                                            :label="value.label"
                                            v-model="templateVariables[key].value"
                                        ></v-text-field-v1>
                                    </div>
                                </template>
                            </div>
                         </div>
                         <div v-if="form.errors.custom_error" class="fs-1 pb-2 text-error">
                             {{ form.errors.custom_error }}
                         </div>
                     </div>
                 </v-card-text>
                 <v-card-actions-form>
                     <div class="d-flex flex-grow-1 justify-space-between">
                         <v-btn-input-compact color="btn-secondary" @click="helpers.goBack">
                             <template #prepend>
                                 <mdi-icon>chevron_left</mdi-icon>
                             </template>
                             Back
                         </v-btn-input-compact>
                         <v-btn-input-compact type="submit" :disabled="form.processing" :loading="form.processing">
                             <template #prepend>
                                 <mdi-icon>check</mdi-icon>
                             </template>
                             Save
                         </v-btn-input-compact>
                     </div>
                     
                 </v-card-actions-form>
             </v-card>
         </v-form>
     </div>
 </template>
 <script setup lang="ts">
     import { ref } from "vue";
     import { useForm, usePage, router } from '@inertiajs/vue3';
     import { toast } from 'vue3-toastify';
     import { SharedPage, SharedPageProps } from '@/Types/inertia';
     import { User } from '@/Types/Models/user';
     import helpers from '@/Plugins/helpers';
     import { pageTitle, containerGapForForm } from '@/Composables/PageMeta';
     import { Company } from "@/Types/Models/company";
     import { Biller, TemplateVariable } from "@/Types/Models/biller";
     import { useDate } from 'vuetify';
     import { useAuth } from '@/Composables/Auth';

 
     pageTitle.value = 'Add Company';
     containerGapForForm();
     interface PageData extends SharedPageProps{
         data?:{
            company?: Company,
            biller?: Biller,
            success_redirect?: string
         }
     }

     interface Form extends Company{
         custom_error: string,
         success_redirect: string,
         template_variables: TemplateVariable,
     }
 
     const page = usePage<PageData>();
     const company = ref<Company>(page.props.data?.company ?? {} as Company);
     const biller = ref<Biller>(page.props.data?.biller ?? {} as Biller);
     const formRef = ref<HTMLFormElement | null>(null);
     const formValid = ref<boolean | null>(false);
     const form = useForm<Form>({
         custom_error: '',
         name: company.value?.name ?? '',
         street_address: company.value?.street_address ?? '',
         city: company.value?.city ?? '',
         state: company.value?.state ?? '',
         postal_code: company.value?.postal_code ?? '',
         country: company.value?.country ?? '',
         placeholder_name: company.value?.placeholder_name ?? '',
         placeholder_email: company.value?.placeholder_email ?? '',
         template_variables: {},
         success_redirect: page.props.data?.success_redirect ?? '',
     });

     const templateVariables = ref<TemplateVariable>(!Object.keys(biller.value?.additional_data?.template_variables ?? {}).length ? {} : biller.value?.additional_data?.template_variables);
     const companytemplateVariables = ref<TemplateVariable>(!Object.keys(company.value?.additional_data?.template_variables ?? {}).length ? {} : company.value?.additional_data?.template_variables);
    Object.keys(companytemplateVariables.value).forEach(key => {
        if(templateVariables.value[key]){
            templateVariables.value[key].value = companytemplateVariables.value[key].value;
        }
    });

 
     const store = async () => {
         console.log(form);
         var validated = await formRef.value?.validate();
         console.log(validated, formValid.value);
         if(validated?.valid){
            form.clearErrors();
            for (const [key, value] of Object.entries(templateVariables.value)) {
                if(value.map && value.map != 'user_input'){
                    continue;
                }
                let v = '';
                if(value.value){
                    v = value.value.trim();
                }
                if(!v){
                    form.setError('custom_error', 'You need to fill in every field under Agreement Information');
                    return;
                }
            }
            form.template_variables = templateVariables.value;
            form.post(route('companies.store', company.value?.id ?? 0), {
                 preserveState: true,
                 onSuccess: (page: SharedPage) => {
                     toast.success(page.props?.jetstream?.flash?.msg?.text ?? 'Company is saved');
                     formRef.value?.resetValidation();
                     form.clearErrors();
                 },
                 onFinish: (visit) => {
                     // console.log('visit onFinish', visit);
                 },
                 onError: (errors) => {
                     console.log('onError', errors);
                     formRef.value?.resetValidation();
                 }
             });
         }
         else{
             form.setError('custom_error', 'There are invalid data in the form, please check once again');
         }
     }
 </script>